import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Badge } from '@optools/components/ui/badge';
import { GET_SESSION_OVERRIDES } from '@optools/services/operator-gateway/overrides/session/queries';
import { SessionOverride } from '@optools/services/operator-gateway/overrides/session/types';
import { CreateSessionOverride } from '@optools/tools/overrides/components/create-session-override';
import { DeleteSessionOverride } from '@optools/tools/overrides/components/delete-session-override';
import { UpdateSessionOverride } from '@optools/tools/overrides/components/update-session-override';
import { transformSessionOverride } from '@optools/utils/overrides/session';
import { createFileRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

export const Route = createFileRoute('/_auth/overrides/session')({
  component: RouteComponent,
});

export function RouteComponent() {
  const { data, loading } = useQuery(GET_SESSION_OVERRIDES);

  const overrides = data?.sessionOverrides?.map(transformSessionOverride) ?? [];

  const columns = useMemo<ColumnDef<SessionOverride>[]>(
    () => [
      {
        accessorKey: 'id',
        header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
        id: 'ID',
      },
      {
        accessorKey: 'userId',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Okta ID" />,
        id: 'Okta ID',
      },
      {
        accessorKey: 'clientType',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Client Type" />,
        id: 'Client Type',
      },
      {
        accessorFn: (row) => row.features.join(' '),
        accessorKey: 'features',
        cell: ({ cell }) => {
          return (
            <div className="flex flex-wrap gap-2 py-1">
              {cell.row.original.features.map((feature) => (
                <Badge key={feature} variant="secondary">
                  {feature}
                </Badge>
              ))}
            </div>
          );
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Features" />,
        id: 'Features',
        size: 300,
      },
      {
        cell: ({ cell }) => {
          return (
            <div className="flex items-center justify-end gap-2">
              <UpdateSessionOverride override={cell.row.original} />
              <DeleteSessionOverride overrideId={cell.row.original.id} />
            </div>
          );
        },
        header: () => <span className="flex justify-end">Actions</span>,
        id: 'actions',
      },
    ],
    [],
  );

  if (loading) {
    return <PageLoader />;
  }

  return (
    <DataTable columns={columns} data={overrides}>
      <CreateSessionOverride />
    </DataTable>
  );
}
